// This file is just to prepare a function that can be called anywhere.

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AppConfig } from 'app/config/service.config';
import { Observable, ReplaySubject, map } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { LogService } from '../logging/log.service';
import { Corporate, CorporateRequest, OperatingHours, Pagination } from './organization.types';
import { BusinessType, CorporateStatus, IndustryType, Regions, States } from './organization.enum';

@Injectable({ providedIn: 'root' })
export class OrganizationService {
    // -----------------------------------------------------------------------------------------------------
    // @ Private variables
    // -----------------------------------------------------------------------------------------------------
    private _httpClient = inject(HttpClient);
    private _apiServer = inject(AppConfig);
    private _authService = inject(AuthService);
    private _logging = inject(LogService);

    // Pagination
    private _pagination: ReplaySubject<Pagination | null> = new ReplaySubject<Pagination | null>(null);

    private _corporates: ReplaySubject<Corporate[]> = new ReplaySubject<Corporate[]>(1);

    private _allCorporateNames: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

    // ---------------------------------------------------------------------------------------------suppliers
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    // pagination
    get pagination$(): Observable<Pagination> {
        return this._pagination.asObservable();
    }

    // Setter & getter for corporates
    set corporates(value: Corporate[]) {
        this._corporates.next(value);
    }

    get corporates$(): Observable<Corporate[]> {
        return this._corporates.asObservable();
    }

    get allCorporateNames$(): Observable<any[]> {
        return this._allCorporateNames.asObservable();
    }

    private get baseUrl(): string {
        // Assuming eastelService is .../api/v1, we want .../api
        // If eastelService is https://api.eastel.asia/api/v1
        // We want to call https://api.eastel.asia/api/v2/...
        //return "http://localhost:9087/api/v1";
        return this._apiServer.settings.apiServer.eastelService;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Corporate Methods
    // -----------------------------------------------------------------------------------------------------

    getCorporateById(id: string): Observable<Corporate> {
        return this._httpClient.get<Corporate>(`${this.baseUrl}/corporates/${id}`);
    }

    getCorporates(
        params: {
            page: number;
            size: number;
            sortBy: string;
            sortingOrder: 'DESC' | 'ASC';
            globalSearch: string;
            submitBy: string;
            isActive: boolean;
            isVerified: boolean;
            businessTypes: BusinessType[];
            statuses: CorporateStatus[];
            industryTypes: IndustryType[];
            states: States[];
            regions: Regions[];
            bumiputeraStatus: boolean;
            sstRegistered: boolean;
            excludeCorporateIds?: string[];
        } = {
                page: 0,
                size: 20,
                sortBy: 'createdAt',
                sortingOrder: 'DESC',
                globalSearch: null,
                submitBy: null,
                isActive: null,
                isVerified: null,
                businessTypes: [],
                statuses: [],
                industryTypes: [],
                states: [],
                regions: [],
                bumiputeraStatus: null,
                sstRegistered: null
            }
    ) {
        let accessToken = this._authService.accessToken;

        const requestParams = { ...params };

        // Delete empty values without mutating the caller's filter state.
        Object.keys(requestParams).forEach((key) => {
            if (Array.isArray(requestParams[key])) {
                requestParams[key] = requestParams[key].filter((element) => element !== null);
            }
            if (
                requestParams[key] === null ||
                requestParams[key] === undefined ||
                requestParams[key] === '' ||
                (Array.isArray(requestParams[key]) && requestParams[key].length === 0)
            ) {
                delete requestParams[key];
            }
        });

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                `Bearer ${accessToken}`
            ),
        };

        const { sortingOrder, ...searchParams } = requestParams;
        const request = requestParams.excludeCorporateIds
            ? this._httpClient.post<any>(this.baseUrl + '/corporates/search', {
                ...searchParams,
                sortOrder: sortingOrder
            }, header)
            : this._httpClient.get<any>(this.baseUrl + '/corporates/page', {
                ...header,
                params: requestParams
            });

        return request
            .pipe(
                map((response) => {
                    this._logging.debug(
                        'Response from Organization Service (getCorporates)',
                        response
                    );

                    const pagination = {
                        length: response.totalElements,
                        size: response.size,
                        page: response.page,
                        lastPage: response.totalPages,
                        startIndex: response.page * response.size,
                        endIndex: Math.min(
                            response.page * response.size + response.size - 1,
                            response.totalElements - 1
                        ),
                    };

                    this._pagination.next(pagination);
                    this._corporates.next(response.content);

                    return response.content;
                })
            );
    }

    checkRegistrationNumber(registrationNumber: string = null) {
        let accessToken = this._authService.accessToken;

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                `Bearer ${accessToken}`
            ),
        };

        return this._httpClient
            .get<any>(
                this.baseUrl + '/corporates/registration/' + registrationNumber + '/exists',
                header
            )
            .pipe(
                map((response) => {
                    this._logging.debug(
                        'Response from Organization Service (checkRegistrationNumber)',
                        response
                    );

                    return response;
                })
            );
    }

    updateCorporate(body: CorporateRequest, id: string) {
        let accessToken = this._authService.accessToken;

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                `Bearer ${accessToken}`
            ),
        };

        return this._httpClient
            .put<any>(
                this.baseUrl + '/corporates/' + id,
                body,
                header
            )
            .pipe(
                map((response) => {
                    this._logging.debug(
                        'Response from Organization Service (updateCorporate)',
                        response
                    );

                    return response;
                })
            );
    }

    getAllCorporateNames() {
        let accessToken = this._authService.accessToken;

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                `Bearer ${accessToken}`
            ),
        };

        return this._httpClient
            .get<any[]>(this.baseUrl + '/corporates/all-names', header)
            .pipe(
                map((response) => {
                    this._logging.debug(
                        'Response from Organization Service (getAllCorporateNames)',
                        response
                    );
                    this._allCorporateNames.next(response);
                    return response;
                })
            );
    }
}
